import { useState, useEffect } from 'react';

interface UseIsInViewportOptions {
  /**
   * Value between 0 and 1 indicating what percentage of the element must be visible
   * @default 0.1
   */
  threshold?: number;

  /**
   * Margin around the root element
   * @default '0px'
   */
  rootMargin?: string;
}

/**
 * Custom hook that determines if an element with the given ID is currently in the viewport
 * @param elementId - The ID of the element to check
 * @param options - Configuration options
 * @returns Whether the element is in viewport
 */
export const useIsInViewport = (elementId: string, options: UseIsInViewportOptions = {}): boolean => {
  const [isInViewport, setIsInViewport] = useState<boolean>(false);

  useEffect(() => {
    const element = document.getElementById(elementId);

    if (!element) {
      console.warn(`Element with ID "${elementId}" not found.`);
      return () => {};
    }

    const observerOptions: IntersectionObserverInit = {
      root: null, // Use the viewport as the root
      rootMargin: options.rootMargin || '0px',
      threshold: options.threshold || 0.1, // 10% of the element must be visible by default
    };

    const observer = new IntersectionObserver(entries => {
      // We're only observing one element, so we can just check the first entry
      const [entry] = entries;
      setIsInViewport(entry.isIntersecting);
    }, observerOptions);

    observer.observe(element);

    // Clean up the observer when component unmounts or elementId changes
    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [elementId, options.threshold, options.rootMargin]);

  return isInViewport;
};
